/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';

import { DAY_ORDER } from '../../../helpers/schedule-tag-format';
import WorkspaceScheduleDayRow from './WorkspaceScheduleDayRow';

/**
 * The seven day rows of a Custom_Schedule (Requirement 5.3).
 *
 * The sequence comes from `DAY_ORDER` in the grammar helper rather than being spelled here, so that
 * the grid, the tag builder and the parser cannot disagree about which day comes first. Every row is
 * rendered, including the days that are off, because a day missing from the grid would read as a day
 * that cannot be scheduled rather than one that currently is not.
 *
 * Presentational only: the days arrive as a prop and every change is reported through `onDayChange`.
 * The Schedule_Modal owns the observable state, so this component holds none and does no data
 * fetching. It adds nothing of its own between the row and the modal: the row reports the whole next
 * `{ kind, start, stop }` and this component passes it on with the day it belongs to.
 *
 * Props:
 * - days: { MON: { kind, start, stop }, ... } keyed by the values in DAY_ORDER
 * - timeOptions: [{ key, value, text }] from `schedule-display.js`, passed through to each row
 * - onDayChange: (day, nextValue) => void
 * - disabled: boolean
 */

class WorkspaceScheduleDayGrid extends React.Component {
  // Curried so that each row keeps a stable handler for its own day, matching the handler shape used
  // by the sibling components in this directory.
  handleDayChange = day => nextValue => {
    if (this.props.disabled) return;

    const onDayChange = this.props.onDayChange;
    if (onDayChange) onDayChange(day, nextValue);
  };

  render() {
    const days = this.props.days || {};
    const disabled = !!this.props.disabled;

    return (
      <div className="wsch-modal-day-grid" role="group" aria-label="Days and times">
        {DAY_ORDER.map(day => (
          <WorkspaceScheduleDayRow
            key={day}
            day={day}
            // An absent entry is left to the row, which treats a missing value as off using the same
            // defaults as the grammar helper. Substituting a value here would put a second copy of
            // that decision in the tree.
            value={days[day]}
            timeOptions={this.props.timeOptions}
            onChange={this.handleDayChange(day)}
            disabled={disabled}
          />
        ))}
      </div>
    );
  }
}

export default WorkspaceScheduleDayGrid;
